import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

let idSeq = 0;
const positions = new Set([
  "top-center","top-right","bottom-right","bottom-center","bottom-left","top-left"
]);

const ToastContext = createContext({
  show: (_msg, _opts) => {},
  success: (_msg, _opts) => {},
  error: (_msg, _opts) => {},
  warning: (_msg, _opts) => {},
  info: (_msg, _opts) => {},
  dismiss: (_id) => {},
  clear: () => {},
  toasts: [],
});

export function ToastProvider({
  children,
  position = "top-center",
  duration = 5000,
  maxToasts = 3,
}) {
  const pos = positions.has(position) ? position : "top-center";
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const schedule = useCallback((id, d) => {
    const start = Date.now();
    const timeoutId = setTimeout(() => {
      timersRef.current.delete(id);
      setToasts(t => t.filter(x => x.id !== id));
    }, d);
    timersRef.current.set(id, { timeoutId, remaining: d, start });
  }, []);

  const pauseTimer = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (!t) return;
    clearTimeout(t.timeoutId);
    const elapsed = Date.now() - t.start;
    timersRef.current.set(id, { ...t, remaining: Math.max(0, t.remaining - elapsed) });
  }, []);

  const resumeTimer = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (!t) return;
    schedule(id, Math.max(0, t.remaining));
  }, [schedule]);

  const dismiss = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t.timeoutId);
      timersRef.current.delete(id);
    }
    setToasts(curr => curr.filter(x => x.id !== id));
  }, []);

  const clear = useCallback(() => {
    timersRef.current.forEach(({ timeoutId }) => clearTimeout(timeoutId));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  const baseShow = useCallback((message, opts = {}) => {
    const id = ++idSeq;
    const toast = {
      id,
      message: message ?? "",
      type: opts.type ?? "success",
      duration: Number.isFinite(opts.duration) ? opts.duration : duration,
      closable: opts.closable !== false,
    };

    setToasts(curr => {
      const next = [...curr, toast];
      if (next.length > maxToasts) next.splice(0, next.length - maxToasts);
      return next;
    });

    if (toast.duration > 0) schedule(id, toast.duration);
    return id;
  }, [duration, maxToasts, schedule]);

  const show = baseShow;
  const success = useCallback((m, o = {}) => baseShow(m, { ...o, type: "success" }), [baseShow]);
  const error   = useCallback((m, o = {}) => baseShow(m, { ...o, type: "error" }), [baseShow]);
  const warning = useCallback((m, o = {}) => baseShow(m, { ...o, type: "warning" }), [baseShow]);
  const info    = useCallback((m, o = {}) => baseShow(m, { ...o, type: "info" }), [baseShow]);

  const value = useMemo(() => ({
    show, success, error, warning, info, dismiss, clear, toasts, position: pos,
    _pause: pauseTimer, _resume: resumeTimer
  }), [show, success, error, warning, info, dismiss, clear, toasts, pos, pauseTimer, resumeTimer]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
